<template>
  <available-section section="aggregator-integrations">
    <main class="integration">
      <div class="integration__container">
        <button-back />
        <h1 class="integration__title">Настройка интеграции</h1>
        <p class="integration__subtitle">
          Вы можете настроить интеграцию с порталом 32top.kz, чтобы получать больше новых пациентов.
          Расписание клиники будет выводиться на 32top,
          а заявки с сайта будут записываться сразу в МИС. <br><br>
          Для этого вам необходимо ввести Ключ в
          вашем личном кабинете на сайте
          <a
            href="https://www.32top.kz"
            rel="noopener noreferrer"
            target="_blank">32top.kz</a> в разделе &#171;Онлайн-расписание&#187;. <br>
          Не забудьте проверить, чтобы на 32top к вашей клинике были привязаны врачи из списка ниже.
          <br>
          Вы можете связаться с нами &mdash; мы поможем вам быстро подключить интеграцию.
        </p>
        <div
          v-if="isLoaded"
          class="integration__connection"
        >
          <dl class="integration__definition">
            <dt class="integration__definition__label">Название клиники</dt>
            <dd class="integration__definition__count">{{ clinicName }}</dd>
          </dl>
          <input-text
            placeholder="https://www.32top.kz/clinics/######"
            title="Ссылка на страницу стоматологии на 32top.kz"
            v-bind:violations="violationCauses['url']"
            v-mask="urlMask"
            v-model="clinicUrl"
          />
          <input-checkbox
            class="integration__qr-code"
            title="Печатать QR-код"
            v-model="isPrintQrCode"
          />
          <button
            v-if="!hasIntegration"
            class="integration__button"
            v-on:click="onSubmitConnect"
            type="button"
          >
            Включить интеграцию
          </button>
          <button
            v-else
            class="integration__button"
            v-on:click="onEditConnect"
            type="button"
          >
            Изменить
          </button>
        </div>
        <section
          class="integration__control"
          v-if="hasIntegration"
        >
          <dl class="integration__definition">
            <dt class="integration__definition__label">Ключ</dt>
            <dd class="integration__definition__key">
              {{ integrationKey }}
              <button
                class="integration__definition__copy"
                title="Скопировать ключ"
                type="button"
                v-on:click="copyKeyToClipboard"
              />
            </dd>
          </dl>
          <div class="integration__table">
            <div class="integration__table__head">
              <div class="integration__table__cell">№</div>
              <div class="integration__table__cell">ФИО врача</div>
              <div class="integration__table__cell align">Интеграция</div>
            </div>
            <div
              class="integration__table__row"
              v-for="employee in employees"
              v-bind:key="employee.getId()">
              <div data-label="№" class="integration__table__cell number" />
              <div data-label="ФИО врача" class="integration__table__cell">
                {{ employee.getFullName() }}
              </div>
              <div data-label="Интеграция" class="integration__table__cell align">
                <input
                  aria-label="Включить в интеграцию"
                  type="checkbox"
                  class="integration__checkbox"
                  v-bind:checked="employee.getIsIntegrated()"
                  v-on:change="onSwitchEmployee(employee.getId(), $event)"
                  :id="`checkbox-integration${employee.getId()}`"
                />
                <label :for="`checkbox-integration${employee.getId()}`" />
              </div>
            </div>
          </div>
        </section>
        <support-contacts class="integration__contacts" title="Если у вас возникли вопросы, свяжитесь с нами:" />
      </div>
    </main>
  </available-section>
</template>
<script>

import clipboardMixin from '@/mixins/clipboardMixin';
import notifierMixin from '@/mixins/notifierMixin';
import SupportContacts from '@/components/SupportContacts.vue';
import { validateUrl32top } from '@/inputConstraint';
import ViolationList from '@/utils/violationList';
import violations from '@/violations.json';
import AvailableSection from '@/views/ClinicPanel/AvailableSection.vue';
import InputCheckbox from '@/components/InputCheckbox.vue';
import InputText from '@/components/InputText.vue';
import ButtonBack from '@/components/ButtonBack.vue';

/**
 * @description Компонент интеграции с 32top.
 * @type {Vue}
 */
export default {
  components: {
    ButtonBack,
    AvailableSection,
    InputCheckbox,
    InputText,
    SupportContacts,
  },
  computed: {
    /**
     * @description Имеет интеграцию.
     * @type {boolean}
     */
    hasIntegration() {
      return this.integrationKey !== '';
    },
  },
  created() {
    this.fetchIntegration();
  },
  data() {
    return {
      /**
       * @description Сотрудники рабочего расписания.
       * @type {Employee[]}
       */
      employees: [],
      /**
       * @description Текст ошибки.
       * @type {string}
       */
      errorText: '',
      /**
       * @description Ключ интеграции.
       * @type {string}
       */
      integrationKey: '',
      /**
       * @description Печатать QR-код.
       * @type {boolean}
       */
      isPrintQrCode: true,
      /**
       * @type {string}
       */
      clinicName: '',
      /**
       * @type {string}
       */
      clinicUrl: '',
      /**
       * @type Array
       */
      violationCauses: [],
      /**
       * @description Данные загружены.
       * @type {boolean}
       */
      isLoaded: false,
    };
  },
  methods: {
    urlMask(value) {
      if (!value) {
        return [''];
      }
      const url = 'https://www.32top.kz/clinics/';
      const regexp = /^https:\/\/([a-z]*.)?32top.kz\/clinics\/[0-9]{0,8}/u;
      const result = regexp.exec(value);
      if (result !== null) {
        return [result[0]];
      }
      const id = parseInt(value, 10);
      if (Number.isNaN(id) !== true) {
        return [url, id];
      }
      return [value];
    },
    /**
     * @description Скопировать ключ в буфер обмена.
     * @returns {void}
     */
    copyKeyToClipboard() {
      this.toClipboard(this.integrationKey).then(
        () => { this.notifySuccess('Ключ успешно скопирован.'); },
        () => { this.notifyFail('Не удалось скопировать ключ.'); },
      );
    },
    /**
     * @description Получить ключ интеграции.
     */
    fetchIntegration() {
      const onFulfilled = (result) => {
        result.forEach((item) => {
          this.clinicUrl = item.link;
          this.clinicName = item.name;
          this.employees = item.employees;
          this.integrationKey = item.key;
          this.isPrintQrCode = item.isPrintQrCode;
        });
        result.forEachLeft(({ title }) => {
          this.errorText = title;
        });
      };
      this
        .$aggregatorIntegrationGateway
        .get32top()
        .then(onFulfilled)
        .finally(() => { this.isLoaded = true; });
    },
    /**
     * @description Обработать результат переключения сотрудника.
     * @param {Either} result Результат.
     * @returns {void}
     */
    onResultSwitchEmployee(result) {
      result.forEach(({ title }) => {
        this.notifySuccess(title);
      });
      result.forEachLeft(({ title }) => {
        this.notifyFail(title);
        this.employees = [];
        this.fetchIntegration();
      });
    },
    /**
     * @description Обработать подключение к интеграции.
     * @returns {void}
     */
    onSubmitConnect() {
      const onFulfilled = (result) => {
        result.forEach(({ key, title }) => {
          this.integrationKey = key;
          this.notifySuccess(title);
          this.fetchIntegration();
        });
        result.forEachLeft(({ title }) => this.notifyFail(title));
      };
      if (this.validateForm()) {
        const params = {
          clinic_url: this.clinicUrl,
          is_print_qr_code: this.isPrintQrCode,
        };
        this
          .$aggregatorIntegrationGateway
          .create32top(params)
          .then(onFulfilled);
      }
    },
    /**
     * @description Обработать изменение интеграции.
     * @returns {void}
     */
    onEditConnect() {
      const onFulfilled = (result) => {
        result.forEach(({ title }) => {
          this.notifySuccess(title);
        });
        result.forEachLeft(({ title }) => this.notifyFail(title));
      };
      if (this.validateForm()) {
        const params = {
          clinic_url: this.clinicUrl,
          is_print_qr_code: this.isPrintQrCode,
        };
        this
          .$aggregatorIntegrationGateway
          .edit32top(params)
          .then(onFulfilled);
      }
    },
    /**
     * Провалидировать форму
     * @return {boolean}
     */
    validateForm() {
      const violationList = new ViolationList();
      if (!this.clinicUrl || !validateUrl32top(this.clinicUrl)) {
        violationList.push('url', violations.settings.url.format);
      }
      this.violationCauses = violationList.getCauses();
      return !violationList.hasCauses();
    },
    /**
     * @description Обработать переключение включенности сотрудника.
     * @param {number} employeeId Идентификатор сотрудника.
     * @param {Event}  event      Событие.
     */
    onSwitchEmployee(employeeId, event) {
      const isEnabled = event.target.checked;
      if (isEnabled) {
        this
          .$aggregatorIntegrationGateway
          .enableEmployee(this.integrationKey, employeeId)
          .then(this.onResultSwitchEmployee);
      } else {
        this
          .$aggregatorIntegrationGateway
          .disableEmployee(this.integrationKey, employeeId)
          .then(this.onResultSwitchEmployee);
      }
    },
  },
  mixins: [
    clipboardMixin,
    notifierMixin,
  ],
  name: 'ClinicPanelIntegration32top',
};
</script>
<style lang="scss">
@use "@/scss/copy-icon";
@use "@/scss/buttons";

.integration {
  width: 100%;
  height: 100%;
  overflow: scroll;
  &__container {
    padding: 38px 20px 200px 23px;
  }
  &__title {
    font-weight: 600;
    font-size: 38px;
    line-height: 46px;
    color: #000000;
    margin: 0 0 22px 0;
  }
  &__subtitle {
    display: block !important;
    font-weight: 400;
    font-size: 16px;
    line-height: 28px;
    a:hover {
      text-decoration: none;
    }
  }
  &__connection {
    background-color: var(--section-background-color);
    padding: 40px 50px 35px 40px;
    margin: 46px 0 0 0;
  }
  &__definition {
    &__label {
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      color: var(--unfocus-color);
    }
    &__count {
      font-weight: 400;
      font-size: 16px;
      line-height: 19px;
      color: #192A3E;
      margin: 10px 0 30px 0;
    }
    &__key {
      display: flex;
      align-items: center;
      background-color: var(--primary-background-color);
      border: 1px solid var(--border-color);
      border-radius: 4px;
      padding: 12px 45px 12px 20px;
      position: relative;
      font-size: 21px;
      width: 100%;
    }
    &__copy {
      @extend %copy-icon;
      position: absolute;
      width: 24px;
      height: 24px;
      right: 20px;
    }
  }
  &__button {
    @include buttons.btn-blue(244px, 48px, 16px);
    max-width: 100%;
    margin: 40px 0 0 0;
    &:disabled {
      background-color: var(--unfocus-color);
    }
  }
  &__control {
    border-top: 1px solid #C2CFE0;
    background-color: var(--section-background-color);
    padding: 30px 50px 60px 40px;
  }
  &__table {
    counter-reset: counterEmployees;
    background: #FFFFFF;
    margin: 35px 0 0 0;
    &__head,
    &__row {
      align-items: center;
      cursor: pointer;
      display: grid;
      font-size: 14px;
      grid-template-columns: 40px 1fr 138px;
      line-height: 17px;
    }
    &__head {
      font-weight: 700;
      border-bottom: 2px solid #0489E0;
      padding: 10px 0;
    }
    &__row {
      border-bottom: 1px solid #EAEFF3;
      padding: 12px 0;
      &:hover {
        background: #EAEFF3;
      }
    }
    &__cell {
      padding: 0 8px;
      position: relative;
      &.number:after {
        counter-increment: counterEmployees;
        content: counter(counterEmployees);
      }
      &.align {
        text-align: center;
      }
    }
  }
  &__checkbox {
    position: absolute;
    z-index: -1;
    opacity: 0;
    &:checked + label:before {
      background: rgba(4, 137, 224, 0.18);
      border: 1px solid #0489E0;
    }
    &:checked + label:after {
      left: 16px;
      background: #0489E0;
    }
  }
  &__checkbox + label {
  position: relative;
  padding: 0 0 0 38px;
  cursor: pointer;
  }
  &__checkbox + label:before {
    content: '';
    position: absolute;
    top: -4px;
    left: 0;
    width: 38px;
    height: 24px;
    border-radius: 15px;
    background: #EAEFF3;
    border: 1px solid #C2CFE0;
    transition: .2s;
  }
  &__checkbox + label:after {
    content: '';
    position: absolute;
    top: -2px;
    left: 2px;
    width: 20px;
    height: 20px;
    border-radius: 10px;
    background: #90A0B7;
    transition: .2s;
  }
  &__contacts {
    margin: 46px 0 0 0;
  }
  &__qr-code {
    display: block;
    margin-top: 10px;
  }
}

@media screen and (max-width: 1240px) {
  .integration {
    &__container {
      width: 100%;
      padding: 45px 20px 145px;
    }
  }
}
@media screen and (max-width: 640px) {
  .integration {
    &__container {
      padding: 45px 10px 145px;
    }
  }
}
@media screen and (max-width: 600px) {
  .integration {
    &__table {
      &__head {
        display: none;
      }
      &__row {
        grid-template-columns: 1fr;
        grid-gap: 10px;
        margin: 0 0 20px 0;
      }
      &__cell {
        display: flex;
        justify-content: space-between;
        text-align: right;
        font-size: 12px;
      }
      &__cell:before {
        content: attr(data-label);
        font-weight: 700;
        margin-right: 20px;
        font-size: 10px;
        text-align: left;
      }
    }
  }
}
@media screen and (max-width: 480px) {
  .integration {
    &__connection {
      background-color: transparent;
      padding: 0 0 20px 0;
    }
    &__control {
      padding: 20px 0 0 0;
      background-color: transparent;
    }
  }
}
</style>
